import type { StreamingChatId } from "@storytell/streaming";
import { createMemo } from "solid-js";
import { useChatScrollContextSafe } from "~/components/chat/contexts/ChatScrollContext";
import { isActivityOperation } from "~/lib/chat/activityFilter";
import { useWire } from "~/wire";

/**
 * Activity operations of the message the reader is looking at — what the side panel's Activity tab
 * renders, and the only thing that tab has to show. Requires ChatScrollProvider; outside a chat the
 * count is 0.
 */
export const useChatActivity = () => {
  const wire = useWire();
  const chatScroll = useChatScrollContextSafe();

  const chatId = createMemo((): StreamingChatId | undefined => chatScroll?.chatId() ?? undefined);

  // The message crossing the viewport's center line; the last message until one is observed.
  const activeMessageId = createMemo(() => {
    const scrollMsg = chatScroll?.activeMessageId();
    if (scrollMsg) return scrollMsg;
    const cid = chatId();
    if (!cid) return undefined;
    const chat = wire.stream.chat.get(cid);
    return chat?.messages.at(-1);
  });

  const opsCount = createMemo(() => {
    const msgId = activeMessageId();
    if (!msgId) return 0;
    const msg = wire.stream.message.get(msgId);
    if (!msg) return 0;
    let count = 0;
    const operations = msg.operations ?? [];
    for (let i = 0; i < operations.length; i++) {
      const op = wire.stream.operation.get(operations[i]);
      if (op && isActivityOperation(op.activity)) count++;
    }
    return count;
  });

  return { chatId, activeMessageId, opsCount };
};
